import api from './api';
import { CheckoutData, CheckoutListResponse, CheckoutHistoryItem, CheckoutEmission } from '../interfaces/Checkout';

export const checkoutService = {
  async getCheckouts(page: number = 1, limit: number = 10, status?: string): Promise<CheckoutListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (status) {
      params.append('status', status);
    }

    const response = await api.get(`/read/checkout/user/me/checkouts?${params}`);
    return response.data;
  },

  async getCheckoutById(id: string): Promise<CheckoutData> {
    const response = await api.get(`/checkout/${id}`);
    const data = response.data;
    // Normaliza campo de origem do voo: backend legado retorna 'flight_origin',
    // versão nova retorna 'flightSource'. Garante que ambos estejam presentes.
    if (!data.flightSource && data.flight_origin) {
      data.flightSource = data.flight_origin;
    }
    if (!data.flight_origin && data.flightSource) {
      data.flight_origin = data.flightSource;
    }
    return data;
  },

  async getAllCheckouts(
    page: number = 1,
    limit: number = 10,
    filters?: {
      status?: string;
      paymentStatus?: string;
      id?: string;
      passengerName?: string;
      routeOutbound?: string;
      routeReturn?: string;
      minValue?: string;
      maxValue?: string;
      dateFrom?: string;
      dateTo?: string;
    },
  ): Promise<CheckoutListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters?.status)        params.append('status', filters.status);
    if (filters?.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
    if (filters?.id)            params.append('id', filters.id);
    if (filters?.passengerName) params.append('passengerName', filters.passengerName);
    if (filters?.routeOutbound) params.append('routeOutbound', filters.routeOutbound);
    if (filters?.routeReturn)   params.append('routeReturn', filters.routeReturn);
    if (filters?.minValue)      params.append('minValue', filters.minValue);
    if (filters?.maxValue)      params.append('maxValue', filters.maxValue);
    if (filters?.dateFrom)      params.append('dateFrom', new Date(filters.dateFrom).toISOString());
    if (filters?.dateTo)        params.append('dateTo', new Date(filters.dateTo + 'T23:59:59').toISOString());

    const response = await api.get(`/checkout?${params}`);
    return response.data;
  },

  async updateCheckoutStatus(id: string, status: string, updatedBy: string): Promise<CheckoutData> {
    const response = await api.patch(`/checkout/${id}/status`, {
      status,
      updatedBy
    });
    return response.data;
  },

  async updatePaymentStatus(id: string, paymentStatus: 'processando' | 'aprovado' | 'reprovado' | 'reembolsado', updatedBy: string): Promise<CheckoutData> {
    const response = await api.patch(`/checkout/${id}/payment-status`, {
      paymentStatus,
      updatedBy
    });
    return response.data;
  },

  async addLocalizador(id: string, data: {
    localizador?: string;
    leg?: 'full' | 'outbound' | 'inbound';
    updatedBy: string;
    partnerName?: string;
    partnerPaymentMethod?: string;
    costPerAdult?: number;
    costPerChild?: number;
    boardingFee?: number;
    totalCost?: number;
  }): Promise<CheckoutData> {
    const response = await api.patch(`/checkout/${id}/localizador`, data);
    return response.data;
  },

  async getEmission(checkoutId: string): Promise<CheckoutEmission> {
    const response = await api.get(`/checkout/${checkoutId}/emission`);
    console.log('[getEmission] response.data:', response.data);
    return response.data;
  },

  async getWhatsappEvents(id: string): Promise<any> {
    const response = await api.get(`/checkout/${id}/whatsapp-events`);
    return response.data;
  },

  async getBoardingPasses(checkoutId: string): Promise<any[]> {
    const response = await api.get(`/checkout/${checkoutId}/boarding-pass`);
    return response.data;
  },

  async uploadBoardingPass(checkoutId: string, formData: FormData): Promise<any> {
    const response = await api.post(`/checkout/${checkoutId}/boarding-pass`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async sendBoardingPass(checkoutId: string, boardingPassId: string): Promise<any> {
    const response = await api.post(`/checkout/${checkoutId}/boarding-pass/send`, { boardingPassId });
    return response.data;
  },

  async deleteBoardingPass(checkoutId: string, boardingPassId: string): Promise<void> {
    await api.delete(`/checkout/${checkoutId}/boarding-pass/${boardingPassId}`);
  },

  async getCheckoutHistory(id: string): Promise<CheckoutHistoryItem[]> {
    try {
      const response = await api.get(`/checkout/${id}/history`);
      return response.data;
    } catch (error: any) {
      // Se a rota de histórico não existir, retorna um histórico simulado baseado nos dados atuais
      console.warn('Histórico não disponível no backend, usando dados simulados');
      return [];
    }
  },
};

export interface MercadoPagoResponseData {
  id?: number;
  status?: string;
  status_detail?: string;
  operation_type?: string;
  payment_type_id?: string;
  payment_method_id?: string;
  payment_method?: {
    id?: string;
    type?: string;
    issuer_id?: string;
  };
  transaction_amount?: number;
  installments?: number;
  currency_id?: string;
  description?: string;
  date_created?: string;
  date_approved?: string;
  date_last_updated?: string;
  date_of_expiration?: string;
  money_release_date?: string;
  money_release_status?: string;
  live_mode?: boolean;
  captured?: boolean;
  binary_mode?: boolean;
  processing_mode?: string;
  issuer_id?: string;
  authorization_code?: string;
  statement_descriptor?: string;
  external_reference?: string;
  notification_url?: string;
  transaction_amount_refunded?: number;
  taxes_amount?: number;
  coupon_amount?: number;
  shipping_amount?: number;
  collector_id?: number;
  card?: {
    last_four_digits?: string;
    first_six_digits?: string;
    expiration_year?: number;
    expiration_month?: number;
    cardholder?: {
      name?: string;
      identification?: {
        type?: string;
        number?: string;
      };
    };
  };
  payer?: {
    id?: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    identification?: {
      type?: string;
      number?: string;
    };
  };
  additional_info?: {
    items?: Array<{
      id?: string;
      title?: string;
      quantity?: string;
      unit_price?: string;
      description?: string;
      category_id?: string;
    }>;
    payer?: {
      first_name?: string;
      last_name?: string;
      phone?: {
        area_code?: string;
        number?: string;
      };
      address?: {
        zip_code?: string;
        street_name?: string;
        street_number?: string;
      };
    };
  };
  transaction_details?: {
    total_paid_amount?: number;
    net_received_amount?: number;
    installment_amount?: number;
    overpaid_amount?: number;
    acquirer_reference?: string;
    financial_institution?: string;
    external_resource_url?: string;
  };
  charges_details?: Array<{
    id?: string;
    name?: string;
    type?: string;
    amounts?: {
      original?: number;
      refunded?: number;
    };
  }>;
  three_ds_info?: {
    creq?: string | null;
    external_resource_url?: string | null;
  };
  refunds?: any[];
  fee_details?: any[];
  point_of_interaction?: {
    type?: string;
    business_info?: {
      unit?: string;
      branch?: string;
      sub_unit?: string;
    };
  };
  [key: string]: any;
}

export interface PaymentData {
  id: number;
  checkoutId: string;
  mercadoPagoId: string;
  paymentMethod: string;
  status: string;
  amount: string | number;
  fee?: string | null;
  netAmount?: string | null;
  pixQrCode?: string | null;
  pixQrCodeBase64?: string | null;
  pixPaymentLink?: string | null;
  externalReference?: string;
  installments?: number;
  cardLastFourDigits?: string;
  cardBrand?: string;
  paymentTypeId?: string;
  billingZipCode?: string;
  billingStreetName?: string;
  billingStreetNumber?: string;
  statement_descriptor_invoice?: string;
  idempotencyKey?: string;
  paidAt?: string | null;
  expiresAt?: string | null;
  webhookData?: MercadoPagoResponseData;
  mercadoPagoResponse?: MercadoPagoResponseData;
  createdAt: string;
  updatedAt: string;
}

export const paymentService = {
  async getPaymentsByCheckout(checkoutId?: string): Promise<PaymentData[]> {
    const response = await api.get(`/payments/checkout/${checkoutId}`);
    return response.data;
  },
};
